/**
 * Store de ubicación con Zustand
 * Sprint 2 - POC GPS Test + Integración Backend
 * 
 * Maneja estado global de:
 * - Ubicación actual
 * - Estado de permisos
 * - Watch/tracking activo
 * - Errores de GPS
 * - Sincronización con backend
 */

import { create } from 'zustand';
import { locationService, GPS_CONFIG, locationApiService } from '../services';
import { getBatteryLevel } from '../services/battery';
import { useManualLocationStore, type ManualCoordinate } from './manualLocationStore';
import { useSystemToolsStore } from './systemToolsStore';
import type {
    LocationData,
    LocationPermissionStatus,
    LocationError,
} from '../types';

interface LocationState {
    // Estado
    location: LocationData | null;
    permissionStatus: LocationPermissionStatus;
    isWatching: boolean;
    error: LocationError | null;
    lastUpdated: number | null;

    // Flags de UI
    isLoading: boolean;
    isRequestingPermission: boolean;

    // Sincronización con backend
    isSyncing: boolean;
    lastSyncedAt: number | null;
    syncError: string | null;
    syncEnabled: boolean;

    // Acciones
    checkPermissions: () => Promise<LocationPermissionStatus>;
    requestPermissions: () => Promise<boolean>;
    getCurrentPosition: () => Promise<LocationData | null>;
    startWatching: (syncToServer?: boolean) => Promise<boolean>;
    stopWatching: () => Promise<void>;
    syncManualLocation: (coordinate: ManualCoordinate) => Promise<void>;
    clearError: () => void;
    reset: () => void;
    setSyncEnabled: (enabled: boolean) => void;
}

// Estado inicial
const initialState = {
    location: null,
    permissionStatus: 'prompt' as LocationPermissionStatus,
    isWatching: false,
    error: null,
    lastUpdated: null,
    isLoading: false,
    isRequestingPermission: false,
    // Sync
    isSyncing: false,
    lastSyncedAt: null,
    syncError: null,
    syncEnabled: true, // Habilitado por defecto
};

// Referencia a la función de cleanup del watch
let watchCleanup: (() => void) | null = null;

// Flag para controlar si sincronizar con el servidor
let shouldSyncToServer = true;

function isValidManualCoordinate(coordinate: ManualCoordinate): boolean {
    return Number.isFinite(coordinate.lat)
        && Number.isFinite(coordinate.lng)
        && coordinate.lat >= -90
        && coordinate.lat <= 90
        && coordinate.lng >= -180
        && coordinate.lng <= 180;
}

function isManualLocationFeatureEnabled(): boolean {
    return useSystemToolsStore.getState().settings?.manual_location_enabled === true;
}

/**
 * Envía ubicación al servidor (con manejo de errores silencioso)
 */
async function syncLocationToServer(
    location: LocationData,
    set: (state: Partial<LocationState>) => void
): Promise<void> {
    set({ isSyncing: true, syncError: null });

    try {
        // Sprint 7: Agregar nivel de batería antes de enviar
        const batteryLevel = location.battery_level === undefined
            ? await getBatteryLevel()
            : location.battery_level;
        const locationWithBattery = { ...location, battery_level: batteryLevel };

        await locationApiService.sendLocation(locationWithBattery);
        set({
            isSyncing: false,
            lastSyncedAt: Date.now(),
            syncError: null,
        });
        console.log('[LocationStore] Location synced to server');
    } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Error de sincronización';
        console.warn('[LocationStore] Sync error:', errorMessage);
        set({
            isSyncing: false,
            syncError: errorMessage,
        });
        // No lanzamos el error para no interrumpir el tracking local
    }
}

export const useLocationStore = create<LocationState>((set, get) => ({
    ...initialState,

    /**
     * Verifica el estado actual de permisos
     */
    checkPermissions: async (): Promise<LocationPermissionStatus> => {
        try {
            const status = await locationService.checkPermissions();
            set({ permissionStatus: status });
            return status;
        } catch (error) {
            console.error('[LocationStore] Error checking permissions:', error);
            return 'prompt';
        }
    },

    /**
     * Solicita permisos de ubicación al usuario
     * @returns true si el permiso fue concedido
     */
    requestPermissions: async (): Promise<boolean> => {
        set({ isRequestingPermission: true, error: null });

        try {
            const status = await locationService.requestPermissions();
            set({
                permissionStatus: status,
                isRequestingPermission: false,
            });

            return status === 'granted';
        } catch (error) {
            const locationError = error as LocationError;
            set({
                error: locationError,
                isRequestingPermission: false,
                permissionStatus: 'denied',
            });
            return false;
        }
    },

    /**
     * Obtiene la ubicación actual una sola vez
     */
    getCurrentPosition: async (): Promise<LocationData | null> => {
        const { permissionStatus } = get();

        // Verificar permisos primero
        if (permissionStatus !== 'granted') {
            const granted = await get().requestPermissions();
            if (!granted) {
                return null;
            }
        }

        set({ isLoading: true, error: null });

        try {
            const location = await locationService.getCurrentPosition(
                GPS_CONFIG.DEFAULT_OPTIONS
            );

            set({
                location,
                lastUpdated: Date.now(),
                isLoading: false,
            });

            return location;
        } catch (error) {
            const locationError = error as LocationError;
            set({
                error: locationError,
                isLoading: false,
            });
            return null;
        }
    },

    /**
     * Inicia el watch de ubicación (actualización continua)
     * @param syncToServer Si es true, envía ubicaciones al servidor (default: true)
     * @returns true si se inició correctamente
     */
    startWatching: async (syncToServer: boolean = true): Promise<boolean> => {
        const { isWatching, permissionStatus } = get();

        // Si ya está watching, no hacer nada
        if (isWatching) {
            console.log('[LocationStore] Already watching');
            return true;
        }

        // Verificar permisos
        if (permissionStatus !== 'granted') {
            const granted = await get().requestPermissions();
            if (!granted) {
                return false;
            }
        }

        // Configurar si sincronizar con servidor
        shouldSyncToServer = syncToServer;

        set({ isLoading: true, error: null });

        try {
            // Callback cuando se obtiene nueva ubicación
            const onSuccess = (location: LocationData) => {
                const { syncEnabled } = get();

                set({
                    location,
                    lastUpdated: Date.now(),
                    error: null,
                });

                // Sincronizar con servidor si está habilitado
                const manualModeEnabled = isManualLocationFeatureEnabled()
                    && useManualLocationStore.getState().manualModeEnabled;
                if (shouldSyncToServer && syncEnabled && !manualModeEnabled) {
                    syncLocationToServer(location, set);
                }
            };

            // Callback de error
            const onError = (error: LocationError) => {
                console.warn('[LocationStore] Watch error:', error);
                set({ error });
            };

            // Iniciar watch
            watchCleanup = await locationService.watchPosition(
                onSuccess,
                onError,
                GPS_CONFIG.DEFAULT_OPTIONS
            );

            set({
                isWatching: true,
                isLoading: false,
            });

            return true;
        } catch (error) {
            const locationError = error as LocationError;
            set({
                error: locationError,
                isLoading: false,
                isWatching: false,
            });
            return false;
        }
    },

    /**
     * Detiene el watch de ubicación
     */
    stopWatching: async (): Promise<void> => {
        if (watchCleanup) {
            watchCleanup();
            watchCleanup = null;
        }

        await locationService.clearWatch();
        shouldSyncToServer = false;
        set({ isWatching: false });
    },

    /**
     * Sincroniza una coordenada simulada por el mismo pipeline del backend.
     */
    syncManualLocation: async (coordinate: ManualCoordinate): Promise<void> => {
        if (!isManualLocationFeatureEnabled() || !isValidManualCoordinate(coordinate)) {
            return;
        }

        const manualLocation: LocationData = {
            latitude: coordinate.lat,
            longitude: coordinate.lng,
            accuracy: 5,
            altitude: null,
            speed: null,
            heading: null,
            battery_level: null,
            timestamp: Date.now(),
        };

        set({
            location: manualLocation,
            lastUpdated: manualLocation.timestamp,
            error: null,
        });

        await syncLocationToServer(manualLocation, set);
    },

    /**
     * Limpia el error actual
     */
    clearError: () => {
        set({ error: null, syncError: null });
    },

    /**
     * Habilita o deshabilita la sincronización con servidor
     */
    setSyncEnabled: (enabled: boolean) => {
        set({ syncEnabled: enabled });
    },

    /**
     * Resetea el store a estado inicial
     */
    reset: () => {
        if (watchCleanup) {
            watchCleanup();
            watchCleanup = null;
        }
        shouldSyncToServer = false;
        set(initialState);
    },
}));
